import { observer } from 'owa-mobx-react';
import folderStore, { getEffectiveFolderDisplayName } from 'owa-folders';
import { getCustomBundledIcon } from 'owa-folders-common/lib/util/getCustomIconComponent';
import { FolderNode } from 'owa-mail-folder-view';
import { showNonFavoriteFolderContextMenu } from 'owa-mail-favorites-store/lib/actions/favoritesContextMenu';
import { getAnchorForContextMenu } from 'owa-positioning';
import { getFolderViewStateFromIdNoFallback } from 'owa-mail-folder-store/lib/selectors/getFolderViewStateFromId';
import { getFolderColor } from 'owa-folder-color-selectors/lib/getFolderColor';
import React from 'react';
import { useResizeObserver } from 'owa-react-hooks/lib/useResizeObserver';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';
import type { FavoritesSideRailItem } from '../types/FavoritesSideRailItem';
import getSiderailFavoriteTooltipText, {
    getSiderailFolderTooltipText,
} from '../util/getSiderailFavoriteTooltipText';
import FavoriteNode from './FavoriteNode';
import { sideRailNodeWrapper } from './FavoritesSideRail.scss';

interface SharedFavoritesSideRailNodeProps {
    itemKey: string;
    hideIcon: boolean;
    onHeightChange: (itemKey: string, height: number) => void;
}

type FavoritesSideRailNodeProps = SharedFavoritesSideRailNodeProps &
    (
        | {
              kind: 'favorite';
              favoriteId: string;
          }
        | {
              kind: 'folder';
              folderId: string;
              treeType: Extract<FavoritesSideRailItem, { kind: 'folder' }>['treeType'];
          }
    );

const FavoritesSideRailNode: React.FunctionComponent<FavoritesSideRailNodeProps> = observer(
    function FavoritesSideRailNode(props) {
        const { itemKey, hideIcon, onHeightChange } = props;
        const wrapperRef = React.useRef<HTMLDivElement>(null);

        const onResize = React.useCallback(
            (bounds: DOMRectReadOnly) => {
                onHeightChange(itemKey, bounds.height);
            },
            [itemKey, onHeightChange]
        );
        useResizeObserver('FavoritesSideRailNode', wrapperRef, onResize);

        const retainedFolderId = props.kind === 'folder' ? props.folderId : undefined;
        const retainedTreeType = props.kind === 'folder' ? props.treeType : undefined;
        const onRetainedFolderContextMenu = React.useCallback(
            (evt: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
                evt.stopPropagation();
                evt.preventDefault();
                const retainedFolder = retainedFolderId
                    ? folderStore().folderTable.get(retainedFolderId)
                    : undefined;
                if (!retainedFolderId || !retainedTreeType || !retainedFolder) {
                    return;
                }
                showNonFavoriteFolderContextMenu(
                    retainedFolderId,
                    retainedTreeType,
                    getAnchorForContextMenu(evt),
                    'FavoritesSiderail'
                );
            },
            [retainedFolderId, retainedTreeType]
        );

        if (props.kind === 'favorite') {
            return (
                <div ref={wrapperRef} className={sideRailNodeWrapper}>
                    <FavoriteNode
                        favoriteId={props.favoriteId}
                        isVerticalNode={true}
                        hideIcon={hideIcon}
                        actionSource="FavoritesSiderail"
                        customTreeNodeTooltip={getSiderailFavoriteTooltipText(props.favoriteId)}
                    />
                </div>
            );
        }

        const folder = folderStore().folderTable.get(props.folderId);
        if (!folder) {
            return null;
        }

        const shouldHideIcon = hideIcon || isWin32StylingEnabled();
        const customIconComponent = shouldHideIcon
            ? undefined
            : getCustomBundledIcon(props.folderId, true);
        const colorId = getFolderViewStateFromIdNoFallback(props.folderId)?.color;
        const customIconColor = colorId ? getFolderColor(colorId) : undefined;

        return (
            <div ref={wrapperRef} className={sideRailNodeWrapper}>
                <FolderNode
                    customIconComponent={customIconComponent}
                    customIconColor={customIconColor}
                    displayName={getEffectiveFolderDisplayName(folder)}
                    folderId={props.folderId}
                    treeType={props.treeType}
                    distinguishedFolderId={folder.distinguishedFolderType ?? ''}
                    totalCount={folder.totalMessageCount}
                    unreadCount={folder.UnreadCount}
                    isVerticalNode={true}
                    hideIcon={shouldHideIcon}
                    customTreeNodeTooltip={getSiderailFolderTooltipText(props.folderId)}
                    onContextMenu={onRetainedFolderContextMenu}
                />
            </div>
        );
    },
    'FavoritesSideRailNode'
);

export default FavoritesSideRailNode;
